import { Link, Navigate } from "react-router-dom";
import { ShieldCheck, Users, LineChart, Lock, KeyRound, ScrollText, Building2 } from "lucide-react";
import { Button, Card } from "@nutrayan/ui";
import { useAuth } from "@/lib/auth";

const CAPABILITIES = [
  {
    icon: Users,
    title: "Staff Registry",
    body: "A dedicated registry for administrators — separate from customers. Invite, assign roles, suspend, and audit staff.",
  },
  {
    icon: LineChart,
    title: "Operational Command",
    body: "Orders, catalog, inventory, and analytics in one workspace, scoped to each admin's responsibilities.",
  },
  {
    icon: ScrollText,
    title: "Security Audit",
    body: "Every sign-in, password change, lock, and admin action is recorded and reviewable by authorized roles.",
  },
];

const SECURITY = [
  {
    icon: KeyRound,
    label: "OTP-based first-time password setup — no admin ever assigns a password",
  },
  { icon: Lock, label: "TOTP multi-factor authentication with enrolment enforcement" },
  { icon: Building2, label: "Strict tenant isolation — admins see only what they govern" },
  { icon: ShieldCheck, label: "Automatic lockout on repeated failures with self-service unlock" },
];

export default function LandingPage() {
  // Signed-in admins skip the marketing page and go straight to their workspace.
  const token = useAuth((s) => s.token);
  if (token) return <Navigate to="/dashboard" replace />;

  return (
    <div className="bg-cream text-ink min-h-screen">
      {/* Top bar */}
      <header className="border-charcoal/10 border-b">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <span className="bg-saffron/15 text-saffron font-display flex h-9 w-9 items-center justify-center rounded-md text-lg">
              N
            </span>
            <span className="font-display text-display-sm text-charcoal">Nutrayan Admin</span>
          </div>
          <Link to="/login">
            <Button size="md">Login</Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-6xl px-6 py-20 text-center">
        <span className="border-saffron/30 bg-saffron/10 text-saffron text-caption inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-medium">
          <ShieldCheck size={13} aria-hidden /> Enterprise-grade access control
        </span>
        <h1 className="font-display text-display-2xl text-charcoal mx-auto mt-6 max-w-3xl">
          The command center for the Nutrayan platform
        </h1>
        <p className="text-body-lg text-ink mx-auto mt-4 max-w-2xl">
          A secure, role-aware administration portal for managing tenants, staff, orders, and
          catalog — built around a zero-password-assignment, multi-factor security model.
        </p>
        <div className="mt-8 flex justify-center gap-3">
          <Link to="/login">
            <Button size="lg">Login to continue</Button>
          </Link>
        </div>
        <p className="text-caption text-muted mt-4">
          Access is invitation-only. New administrators receive a secure email to set their own
          password.
        </p>
      </section>

      {/* Capabilities */}
      <section className="mx-auto max-w-6xl px-6 pb-16">
        <div className="grid gap-6 md:grid-cols-3">
          {CAPABILITIES.map((c) => (
            <Card key={c.title}>
              <div className="bg-forest/10 text-forest flex h-11 w-11 items-center justify-center rounded-md">
                <c.icon size={22} aria-hidden />
              </div>
              <h3 className="font-display text-heading-md text-charcoal mt-4">{c.title}</h3>
              <p className="text-body-sm text-ink mt-2">{c.body}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Security overview */}
      <section className="bg-sand">
        <div className="mx-auto grid max-w-6xl gap-10 px-6 py-16 md:grid-cols-2">
          <div>
            <h2 className="font-display text-display-lg text-charcoal">Security-first by design</h2>
            <p className="text-body text-ink mt-3">
              Administrator credentials are never shared or assigned. Every account sets its own
              password through a verified email code, protected by multi-factor authentication and
              continuous audit logging.
            </p>
          </div>
          <ul className="grid gap-4">
            {SECURITY.map((s) => (
              <li key={s.label} className="flex items-start gap-3">
                <span className="bg-cream text-forest mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md">
                  <s.icon size={16} aria-hidden />
                </span>
                <span className="text-body-sm text-ink">{s.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-6xl px-6 py-20 text-center">
        <h2 className="font-display text-display-lg text-charcoal">
          Ready to manage your platform?
        </h2>
        <p className="text-body text-ink mx-auto mt-3 max-w-xl">
          Sign in with your administrator email to access your workspace.
        </p>
        <div className="mt-6 flex justify-center">
          <Link to="/login">
            <Button size="lg">Login</Button>
          </Link>
        </div>
      </section>

      <footer className="border-charcoal/10 border-t">
        <div className="text-caption text-muted mx-auto max-w-6xl px-6 py-6">
          © Nutrayan. Authorized administrators only.
        </div>
      </footer>
    </div>
  );
}
